// src/components/UI.jsx
import React from "react";

export const PURPLE = "#7F77DD";
export const PINK   = "#D4537E";

// ── Logo ─────────────────────────────────────────────────────────────────
export const Logo = ({ size = 20 }) => (
  <span style={{ fontSize: size, fontWeight: 500, letterSpacing: "-0.5px", display: "flex", alignItems: "baseline", gap: 1 }}>
    <span style={{ color: PINK, fontStyle: "italic" }}>i</span>
    <span style={{ color: "var(--color-text-primary, #111)" }}>Pulse</span>
  </span>
);

// ── Stars ─────────────────────────────────────────────────────────────────
export const Stars = ({ value = 0, size = 13, interactive = false, onChange }) => (
  <span style={{ display: "flex", gap: 2 }}>
    {[1, 2, 3, 4, 5].map((i) => (
      <svg
        key={i} width={size} height={size} viewBox="0 0 24 24"
        fill={i <= Math.round(value) ? "#EF9F27" : "none"}
        stroke="#EF9F27" strokeWidth="1.5"
        style={{ cursor: interactive ? "pointer" : "default" }}
        onClick={() => interactive && onChange && onChange(i)}
      >
        <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/>
      </svg>
    ))}
  </span>
);

// ── Pill button ───────────────────────────────────────────────────────────
export const Pill = ({ label, active, onClick, style = {} }) => (
  <button
    onClick={onClick}
    style={{
      fontSize: 12, padding: "5px 13px", borderRadius: 20, cursor: "pointer",
      whiteSpace: "nowrap", border: `0.5px solid ${active ? PURPLE : "rgba(0,0,0,0.18)"}`,
      background: active ? PURPLE : "white", color: active ? "#fff" : "#555",
      ...style,
    }}
  >{label}</button>
);

// ── Badge ─────────────────────────────────────────────────────────────────
export const Badge = ({ label, bg, tc, style = {} }) => (
  <span style={{ fontSize: 10, padding: "2px 8px", borderRadius: 20, background: bg, color: tc, whiteSpace: "nowrap", ...style }}>
    {label}
  </span>
);

// ── Button ────────────────────────────────────────────────────────────────
export const Btn = ({ children, variant = "primary", onClick, disabled, style = {} }) => {
  const base = { borderRadius: 20, padding: "9px 16px", fontSize: 13, fontWeight: 500, cursor: disabled ? "default" : "pointer", border: "none", ...style };
  if (variant === "primary") return <button onClick={onClick} disabled={disabled} style={{ ...base, background: disabled ? "#ccc" : PURPLE, color: "#fff" }}>{children}</button>;
  if (variant === "outline") return <button onClick={onClick} disabled={disabled} style={{ ...base, background: "none", color: PURPLE, border: `1.5px solid ${PURPLE}` }}>{children}</button>;
  if (variant === "ghost")   return <button onClick={onClick} disabled={disabled} style={{ ...base, background: "none", color: "#666", border: "0.5px solid rgba(0,0,0,0.18)" }}>{children}</button>;
  return <button onClick={onClick} style={base}>{children}</button>;
};

// ── Card ──────────────────────────────────────────────────────────────────
export const Card = ({ children, onClick, style = {} }) => (
  <div
    onClick={onClick}
    style={{ background: "#fff", borderRadius: 14, border: "0.5px solid rgba(0,0,0,0.1)", cursor: onClick ? "pointer" : undefined, ...style }}
  >{children}</div>
);

// ── Screen wrapper ────────────────────────────────────────────────────────
export const Screen = ({ children, style = {} }) => (
  <div style={{ display: "flex", flexDirection: "column", flex: 1, minHeight: 0, ...style }}>
    {children}
  </div>
);

// ── ScrollArea ────────────────────────────────────────────────────────────
export const ScrollArea = ({ children, style = {} }) => (
  <div style={{ flex: 1, overflowY: "auto", WebkitOverflowScrolling: "touch", ...style }}>
    {children}
  </div>
);

// ── Section header ────────────────────────────────────────────────────────
export const SectionHeader = ({ title, action, onAction }) => (
  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "12px 16px 8px" }}>
    <span style={{ fontSize: 13, fontWeight: 500, color: "#111" }}>{title}</span>
    {action && <span onClick={onAction} style={{ fontSize: 11, color: PURPLE, cursor: "pointer" }}>{action}</span>}
  </div>
);

// ── Header bar ────────────────────────────────────────────────────────────
export const Header = ({ left, center, right }) => (
  <header style={{ background: "#fff", padding: "12px 16px 10px", borderBottom: "0.5px solid rgba(0,0,0,0.1)", flexShrink: 0, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
    {left && <div>{left}</div>}
    {center && <div style={{ flex: 1, textAlign: "center" }}>{center}</div>}
    {right && <div style={{ display: "flex", gap: 8, alignItems: "center" }}>{right}</div>}
  </header>
);

// ── Bottom Nav ────────────────────────────────────────────────────────────
export const BottomNav = ({ tabs, active, onChange }) => (
  <nav style={{ background: "#fff", borderTop: "0.5px solid rgba(0,0,0,0.1)", display: "flex", justifyContent: "space-around", padding: "8px 0 16px", flexShrink: 0 }}>
    {tabs.map((t) => (
      <button
        key={t.id} onClick={() => onChange(t.id)}
        style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 2, cursor: "pointer", fontSize: 9, color: active === t.id ? PURPLE : "#999", background: "none", border: "none", padding: "0 5px" }}
      >
        <t.Icon size={20} color={active === t.id ? PURPLE : "#999"} />
        {t.label}
      </button>
    ))}
  </nav>
);

// ── Toast ─────────────────────────────────────────────────────────────────
export const Toast = ({ message }) =>
  message ? (
    <div style={{ position: "fixed", bottom: 80, left: "50%", transform: "translateX(-50%)", background: "#222", color: "#fff", padding: "8px 18px", borderRadius: 20, fontSize: 13, zIndex: 999, pointerEvents: "none", whiteSpace: "nowrap" }}>
      {message}
    </div>
  ) : null;

// ── Gradient hero ─────────────────────────────────────────────────────────
export const GradientHero = ({ grad, height = 180, children }) => (
  <div style={{ height, background: grad, position: "relative", flexShrink: 0, display: "flex", alignItems: "flex-end", padding: 14 }}>
    {children}
  </div>
);
